import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { authAPI } from '../../services/api';
import {
  COACH_NUDGES,
  COACH_TOURS,
  CoachStep,
  CoachTourId,
  firstRouteFor,
  nudgesFor,
  tourForRoute,
} from '../../lib/coachTours';
import { CoachRunMode, countedSlots, currentStep, decideAutoRun, resolveRunSteps, slotsOf, stepsForRun } from '../../lib/coachRun';
import { anchorResolvesNow, readCoachState } from '../../lib/coachDom';
import { anchorBlockedByDialog, isBlockingDialogOpen } from '../../lib/coachDefer';
import { waitUntil } from '../../lib/coachWait';
import { matchRoutePattern } from '../../lib/coachRoutes';
import { onCoachEvent, trackCoach } from '../../lib/coachEvents';
import {
  autoToursSuppressed,
  CoachUserLike,
  CoachWriteDeps,
  getTourRecord,
  isAutoShowEligible,
  nudgeDecision,
  recordNudgeShown,
  recordTourEnd,
} from '../../lib/coachJourney';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useBlockingDialogOpen } from '../../hooks/useBlockingDialogOpen';

/**
 * CoachMarkProvider — state and decisions for the guided tour ("Show me
 * around") and its nudges. Renders no UI; CoachMarkHost owns every pixel.
 *
 * v2 (2026-09-24). Content comes from src/tours/*.md via tours.generated.ts;
 * "seen" lives on the account (lib/coachJourney.ts), not the browser; and
 * three things start a run instead of one:
 *
 *   manual   the account-popover item / task-rail bulb. Page-aware: with no
 *            id it starts whatever tour owns the current page, falling back
 *            to a hop to /apps. Never deferred. Always the full tour.
 *   auto     a journey user (finished onboarding after this shipped) lands on
 *            a page whose tour they have not seen — once per page. Waits for
 *            any real modal to close and for the first anchor to mount.
 *   update   anyone who finished (or closed) a tour at an older version lands
 *            on its page after cards were added — only the new cards, with a
 *            "New" chip.
 *
 * Nudges are single cards that react to a coach event (lib/coachEvents.ts):
 * `showNudge` is called by the matcher effect below after lib/coachJourney's
 * `nudgeDecision` has said yes. A nudge that arrives while a run is open is
 * QUEUED for the same page and shown when the run ends — that is how "Your
 * app is ready" survives the workspace tour the person is halfway through.
 *
 * Never mutate the user's UI to make a card fit (no auto-expanding a
 * collapsed sidebar, no switching a tab). Steps whose anchor is not on
 * screen are dropped; if nothing required resolves, the run does not start.
 */

export type CoachSource = 'manual' | 'auto' | 'update';
export type CoachEndReason = 'completed' | 'dismissed' | 'route';
export type CoachRunKind = 'tour' | 'nudge';

export interface CoachRun {
  kind: CoachRunKind;
  /** Tour id — or the nudge id when kind === 'nudge'. */
  tourId: string;
  /** Steps that resolved when the run started — "N of M" counts these. */
  steps: CoachStep[];
  index: number;
  source: CoachSource;
  mode: CoachRunMode;
  /** Tour version at start (recorded on end); 1 for nudges. */
  version: number;
  /** Captured at start so a mid-run logout still writes the right account. */
  userId: string | null;
  /** Update-mode runs and nudges authored with `new: true`. */
  isNew: boolean;
  /** Nudge trigger name, for analytics. */
  when?: string;
}

export type ShowNudgeResult = 'shown' | 'deferred' | 'queued' | 'skipped';

interface StartTourOptions {
  trigger?: HTMLElement | null;
  source?: CoachSource;
}

interface CoachMarkContextValue {
  run: CoachRun | null;
  /** A real modal is open on top of the run — the Host hides the card and
   *  ring until it closes, then the run resumes at the same step. */
  paused: boolean;
  /** No id = "the tour for the page I am on" (falls back to home). */
  startTour: (id?: CoachTourId | null, opts?: StartTourOptions) => void;
  showNudge: (id: string, opts?: { onShown?: () => void }) => ShowNudgeResult;
  next: () => void;
  back: () => void;
  end: (reason: CoachEndReason) => void;
}

const CoachMarkContext = createContext<CoachMarkContextValue | null>(null);

export function useCoachMark(): CoachMarkContextValue {
  const ctx = useContext(CoachMarkContext);
  if (!ctx) {
    throw new Error('useCoachMark must be used inside CoachMarkProvider');
  }
  return ctx;
}

/** The open run, or null — also null outside the provider, for components
 *  (like the task drawer) that are rendered on their own in tests. */
export function useCoachRun(): CoachRun | null {
  const ctx = useContext(CoachMarkContext);
  return ctx ? ctx.run : null;
}

const NUDGE_QUEUE_MAX = 3;
/** Breathing room between a run closing and a queued nudge opening. */
const NUDGE_DRAIN_DELAY_MS = 400;
/** How long an auto start waits for the page (modal to close, anchor to mount). */
const AUTO_START_WAIT_MS = 60_000;
// Once the first required anchor is on the page, how long to keep waiting for
// the rest before starting without them (a required control that genuinely is
// not on this page must not hold the tour hostage).
const AUTO_START_SETTLE_MS = 1_500;
const NUDGE_START_WAIT_MS = 8_000;

interface QueuedNudge {
  id: string;
  pathname: string;
  onShown?: () => void;
}

function stepFromNudge(id: string): CoachStep | null {
  const nudge = COACH_NUDGES[id];
  if (!nudge) return null;
  return {
    anchor: nudge.anchor,
    side: nudge.side,
    title: nudge.title,
    body: nudge.body,
    since: 1,
    // A nudge is "appear if present": if its target unmounts (the modal it
    // pointed into closes) the Host's optional-step path ends the one-step
    // run instead of falling back to a centred card. See CoachMarkHost's
    // `step.optional && !resolvesNow` branch.
    optional: true,
    ...(nudge.highlight ? { highlight: nudge.highlight } : {}),
  };
}

function viewportWidth(): number {
  return typeof window !== 'undefined' ? window.innerWidth : 0;
}

interface CoachMarkProviderProps {
  children: React.ReactNode;
}

export const CoachMarkProvider: React.FC<CoachMarkProviderProps> = ({ children }) => {
  const { user, mergeUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const userId = user?.id || null;

  const [run, setRun] = useState<CoachRun | null>(null);
  // Bumped whenever a run ends (see the queue-drain effect below) so the
  // auto-start effect gets a fresh `decideAutoRun` check after a nudge that
  // was blocking it closes — see that effect's own comment.
  const [runEpoch, setRunEpoch] = useState(0);
  // A modal opening mid-run (the picked-template modal on /templates opens a
  // beat after the page's anchors mount) must win over the tour. Observed only
  // while a run is live; the auto/nudge starters separately refuse to BEGIN
  // while one is open (see `ready()` below).
  const paused = useBlockingDialogOpen(!!run);
  // The navigate-then-start hop: park the request, navigate, and let the
  // location effect promote it once we have landed on the tour's route.
  const [pending, setPending] = useState<{ tourId: CoachTourId; source: CoachSource } | null>(null);

  // Refs mirror state for callbacks that must read the LATEST value
  // synchronously (event handlers, timers) — and so `end` never has to do
  // its side effects inside a setState updater, which StrictMode runs twice.
  const runRef = useRef<CoachRun | null>(null);
  const pendingRef = useRef(pending);
  pendingRef.current = pending;
  const userRef = useRef<CoachUserLike | null>(null);
  userRef.current = (user as unknown as CoachUserLike) || null;
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;
  const isMobileRef = useRef(isMobile);
  isMobileRef.current = isMobile;

  const triggerRef = useRef<HTMLElement | null>(null);
  const prevRunRef = useRef<CoachRun | null>(null);
  // Separate from prevRunRef (that one drives focus-restore, in its own
  // effect): tracks the non-null -> null transition for the runEpoch bump.
  const prevRunForEpochRef = useRef<CoachRun | null>(null);
  const cancelAutoWaitRef = useRef<(() => void) | null>(null);
  const cancelNudgeWaitRef = useRef<(() => void) | null>(null);
  const nudgeQueueRef = useRef<QueuedNudge[]>([]);

  const deps = useMemo<CoachWriteDeps>(
    () => ({
      setTourState: authAPI.setTourState,
      recordJourneyMilestone: authAPI.recordJourneyMilestone,
      mergeUser: mergeUser as CoachWriteDeps['mergeUser'],
    }),
    [mergeUser],
  );

  const cancelAutoWait = useCallback(() => {
    cancelAutoWaitRef.current?.();
    cancelAutoWaitRef.current = null;
  }, []);
  const cancelNudgeWait = useCallback(() => {
    cancelNudgeWaitRef.current?.();
    cancelNudgeWaitRef.current = null;
  }, []);

  const commitRun = useCallback((next: CoachRun | null) => {
    runRef.current = next;
    setRun(next);
  }, []);

  /**
   * Start a tour run. `presetSteps` lets the auto path pass the exact step
   * subset it decided on (update mode); otherwise the mode picks them.
   * Returns false when nothing could be pointed at — the caller records
   * nothing, so the run is offered again next time.
   */
  const beginTour = useCallback(
    (tourId: CoachTourId, source: CoachSource, mode: CoachRunMode, fromVersion: number, presetSteps?: CoachStep[]): boolean => {
      const tour = COACH_TOURS[tourId];
      if (!tour) return false;
      const authored = presetSteps ?? stepsForRun(tour, mode, fromVersion);
      const steps = resolveRunSteps(authored, anchorResolvesNow);
      if (steps.length === 0) return false;
      commitRun({
        kind: 'tour',
        tourId,
        steps,
        index: 0,
        source,
        mode,
        version: tour.version,
        userId: userRef.current?.id || null,
        isNew: mode === 'update',
      });
      trackCoach('coach_tour_started', { tourId, source, mode, total: countedSlots(slotsOf(steps)).length });
      return true;
    },
    [commitRun],
  );

  /** Ends the current run. Tours are recorded on the account; nudges are not
   *  (they were recorded the moment they were SHOWN, see showNudge). */
  const end = useCallback(
    (reason: CoachEndReason) => {
      const current = runRef.current;
      if (!current) return;
      commitRun(null);
      if (current.kind !== 'tour') return;

      const status = reason === 'completed' ? 'completed' : 'dismissed';
      if (current.userId) {
        const live = userRef.current;
        const target: CoachUserLike = live && live.id === current.userId ? live : { id: current.userId };
        void recordTourEnd(
          target,
          current.tourId,
          { status, version: current.version, lastStepIndex: current.index },
          deps,
        );
      }
      trackCoach('coach_tour_ended', {
        tourId: current.tourId,
        reason,
        lastStep: current.index,
        total: countedSlots(slotsOf(current.steps)).length,
        mode: current.mode,
      });
    },
    [commitRun, deps],
  );

  const startTour = useCallback(
    (id?: CoachTourId | null, opts?: StartTourOptions) => {
      if (opts?.trigger) triggerRef.current = opts.trigger;
      const source: CoachSource = opts?.source || 'manual';
      // A deliberate click beats anything the page was about to do on its own.
      cancelAutoWait();
      cancelNudgeWait();
      if (runRef.current?.kind === 'nudge') end('dismissed');

      const here = tourForRoute(pathnameRef.current);
      const target = (id ?? here ?? 'home') as CoachTourId;
      if (target === here) {
        beginTour(target, source, 'full', 0);
        return;
      }
      // Off-route: hop to where this tour's anchors live. A tour with no
      // static route (the workspace needs a task id) is simply not startable
      // from elsewhere; parking a `pending` we could never promote would
      // wedge the next legitimate start.
      const route = firstRouteFor(target);
      if (!route) return;
      setPending({ tourId: target, source });
      navigate(route);
    },
    [beginTour, cancelAutoWait, cancelNudgeWait, end, navigate],
  );

  // Promote the hop once we have landed.
  useEffect(() => {
    if (!pending) return;
    if (tourForRoute(location.pathname) !== pending.tourId) return;
    const { tourId, source } = pending;
    setPending(null);
    beginTour(tourId, source, 'full', 0);
  }, [pending, location.pathname, beginTour]);

  const showNudge = useCallback(
    (id: string, opts?: { onShown?: () => void }): ShowNudgeResult => {
      const nudge = COACH_NUDGES[id];
      if (!nudge) return 'skipped';
      if (isMobileRef.current) return 'skipped';
      const pathname = pathnameRef.current;
      if (!nudge.routes.some((p) => matchRoutePattern(p, pathname))) return 'skipped';

      if (runRef.current || pendingRef.current) {
        const queue = nudgeQueueRef.current;
        if (queue.length >= NUDGE_QUEUE_MAX || queue.some((q) => q.id === id)) return 'skipped';
        queue.push({ id, pathname, onShown: opts?.onShown });
        return 'queued';
      }

      const step = stepFromNudge(id);
      if (!step) return 'skipped';

      const show = () => {
        cancelNudgeWaitRef.current = null;
        if (runRef.current) return; // something else opened while we waited
        if (pathnameRef.current !== pathname) return;
        commitRun({
          kind: 'nudge',
          tourId: id,
          steps: [step],
          index: 0,
          source: 'auto',
          mode: 'full',
          version: 1,
          userId: userRef.current?.id || null,
          isNew: nudge.isNew,
          when: nudge.when,
        });
        trackCoach('coach_nudge_shown', { id, when: nudge.when });
        opts?.onShown?.();
      };

      // A dialog on top of the page never blocks a nudge whose own anchor
      // lives INSIDE it (the "Use this template" button in the picked-
      // template modal) — only one that would sit hidden behind it.
      const ready = () => anchorResolvesNow(nudge.anchor) && !anchorBlockedByDialog(nudge.anchor);
      if (ready()) {
        show();
        return 'shown';
      }
      // Nudges never render centred — a card pointing at nothing is noise.
      // Wait briefly for the anchor / modal; give up silently past the limit,
      // and since onShown never ran, it is not recorded as seen.
      cancelNudgeWait();
      cancelNudgeWaitRef.current = waitUntil(ready, show, { timeoutMs: NUDGE_START_WAIT_MS });
      return 'deferred';
    },
    [cancelNudgeWait, commitRun],
  );

  const next = useCallback(() => {
    setRun((current) => {
      if (!current || current.index >= slotsOf(current.steps).length - 1) return current;
      const updated = { ...current, index: current.index + 1 };
      runRef.current = updated;
      return updated;
    });
  }, []);

  const back = useCallback(() => {
    setRun((current) => {
      if (!current || current.index <= 0) return current;
      const updated = { ...current, index: current.index - 1 };
      runRef.current = updated;
      return updated;
    });
  }, []);

  // Navigating away ends a tour whose anchors live elsewhere, and any nudge.
  useEffect(() => {
    const current = runRef.current;
    if (!current) return;
    if (current.kind === 'tour' && tourForRoute(location.pathname) === current.tourId) return;
    end('route');
    // end intentionally excluded: this fires on pathname changes only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // A queued nudge belongs to the page it was queued on.
  useEffect(() => {
    nudgeQueueRef.current = nudgeQueueRef.current.filter((q) => q.pathname === location.pathname);
    cancelNudgeWait();
  }, [location.pathname, cancelNudgeWait]);

  // Automatic starts — full for journey users on a page they have not seen,
  // update for anyone the tour has grown past. Decided by pure functions
  // (lib/coachRun.ts, lib/coachJourney.ts); this effect only waits for the
  // page to be ready and then begins.
  useEffect(() => {
    cancelAutoWait();
    if (!userId) return;
    const tourId = tourForRoute(location.pathname);
    if (!tourId) return;
    const tour = COACH_TOURS[tourId];
    const u = userRef.current;
    if (!tour || !u) return;

    const decision = decideAutoRun({
      tour,
      record: getTourRecord(u, tourId),
      newUserEligible: isAutoShowEligible(u, viewportWidth(), location.pathname),
      autoSuppressed: autoToursSuppressed(u),
      isMobile,
      runActive: !!runRef.current,
      pendingActive: !!pendingRef.current,
    });
    if (!decision) return;

    const startedOn = location.pathname;
    const requiredAnchors = decision.steps.filter((s) => !s.optional).map((s) => s.anchor);
    // The page mounts in pieces: the sidebar lands before the composer, a grid
    // lands after its fetch. Starting on the FIRST anchor to appear dropped the
    // not-yet-mounted cards from the run (a 9-card home tour ran as "4 of 4").
    // Wait for every required anchor, but never longer than
    // AUTO_START_SETTLE_MS after the first one showed up — a required anchor
    // that is genuinely absent on this page is dropped, as before.
    let firstSeenAt = 0;
    const ready = () => {
      if (isBlockingDialogOpen()) return false;
      if (requiredAnchors.length === 0) return true;
      const resolved = requiredAnchors.filter((a) => anchorResolvesNow(a)).length;
      if (resolved === 0) {
        firstSeenAt = 0;
        return false;
      }
      if (resolved === requiredAnchors.length) return true;
      if (!firstSeenAt) firstSeenAt = Date.now();
      return Date.now() - firstSeenAt >= AUTO_START_SETTLE_MS;
    };

    cancelAutoWaitRef.current = waitUntil(
      ready,
      () => {
        cancelAutoWaitRef.current = null;
        if (pathnameRef.current !== startedOn) return;
        if (runRef.current || pendingRef.current) return;
        beginTour(tourId, decision.mode === 'update' ? 'update' : 'auto', decision.mode, decision.fromVersion, decision.steps);
      },
      { timeoutMs: AUTO_START_WAIT_MS },
    );
    return cancelAutoWait;
    // user object identity intentionally excluded — re-decide on page / account / viewport changes only.
    // runEpoch IS a dependency: after a nudge ends (e.g. the template modal
    // closed) the page's own tour may now start; decideAutoRun still returns
    // null when a record exists or a run is active, so this cannot restart a
    // tour someone already finished or dismissed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, userId, isMobile, runEpoch]);

  // Nudge matcher: every coach event is offered to the nudges authored for
  // it; the first one lib/coachJourney says yes to is shown (or queued).
  useEffect(
    () =>
      onCoachEvent((detail) => {
        const u = userRef.current;
        if (!u) return;
        const ctx = { viewportWidth: viewportWidth(), pathname: pathnameRef.current };
        for (const nudge of nudgesFor(detail.name)) {
          if (!nudgeDecision(u, nudge, { name: detail.name }, ctx)) continue;
          showNudge(nudge.id, {
            onShown: () => {
              const live = userRef.current || u;
              void recordNudgeShown(live, nudge.id, deps);
            },
          });
          break;
        }
      }),
    [showNudge, deps],
  );

  // Drain the queue when a run ends — one nudge, after a beat. Also bumps
  // runEpoch on a real non-null -> null transition (never on mount) so the
  // auto-start effect below re-checks: a nudge that just closed (e.g. the
  // template modal it pointed into) may have been blocking the page's own
  // tour from starting.
  useEffect(() => {
    const wasActive = !!prevRunForEpochRef.current;
    prevRunForEpochRef.current = run;
    if (run) return;
    if (wasActive) setRunEpoch((n) => n + 1);
    const queue = nudgeQueueRef.current;
    if (queue.length === 0) return;
    const item = queue.shift() as QueuedNudge;
    if (item.pathname !== pathnameRef.current) {
      queue.length = 0;
      return;
    }
    const t = setTimeout(() => showNudge(item.id, { onShown: item.onShown }), NUDGE_DRAIN_DELAY_MS);
    return () => clearTimeout(t);
  }, [run, showNudge]);

  // Per-step analytics (tours only; nudges fire coach_nudge_shown).
  useEffect(() => {
    if (!run || run.kind !== 'tour') return;
    const step = currentStep(run.steps, run.index, readCoachState());
    if (!step) return;
    trackCoach('coach_step_viewed', { tourId: run.tourId, anchor: step.anchor, index: run.index, source: run.source });
  }, [run?.kind, run?.tourId, run?.index]); // eslint-disable-line react-hooks/exhaustive-deps

  // Return focus to whatever opened the tour once it ends — mirrors
  // ConnectorsModalProvider.tsx's triggerRef pattern.
  useEffect(() => {
    if (prevRunRef.current && !run) {
      const t = triggerRef.current;
      if (t && t.isConnected && typeof t.focus === 'function') {
        try { t.focus(); } catch { /* swallow */ }
      }
      triggerRef.current = null;
    }
    prevRunRef.current = run;
  }, [run]);

  // Stop every pending wait on unmount.
  useEffect(
    () => () => {
      cancelAutoWaitRef.current?.();
      cancelNudgeWaitRef.current?.();
    },
    [],
  );

  const value = useMemo<CoachMarkContextValue>(
    () => ({ run, paused, startTour, showNudge, next, back, end }),
    [run, paused, startTour, showNudge, next, back, end],
  );

  // Renders no UI — CoachMarkHost (mounted as a sibling of AppContent in
  // App.tsx) owns every pixel. Keeping render-tree separate from state-tree
  // avoids the trap where adding/removing a provider also adds/removes UI.
  return <CoachMarkContext.Provider value={value}>{children}</CoachMarkContext.Provider>;
};
